import axios, { AxiosError, AxiosResponse } from 'axios';
import config from '../config/env';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - could dispatch logout action here
      console.error('Unauthorized request');
    }
    return Promise.reject(error);
  }
);

// Helper functions that extract content from response
export const apiPost = async <T>(
  url: string,
  body?: unknown,
  customHeaders?: Record<string, string>
): Promise<T> => {
  // For FormData, use axios directly without the default JSON Content-Type
  if (body instanceof FormData) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(config.apiUrl + url, body, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        // Don't set Content-Type - axios will auto-detect with proper boundary
      },
    });
    return response.data?.content ?? response.data;
  }

  const response = await apiClient.post(url, body, {
    headers: customHeaders ? { ...apiClient.defaults.headers.common, ...customHeaders } : undefined,
  });
  return response.data?.content ?? response.data;
};

export const apiGet = async <T>(url: string): Promise<T> => {
  const response = await apiClient.get(url);
  return response.data?.content ?? response.data;
};

export const apiPut = async <T>(url: string, body?: unknown): Promise<T> => {
  const response = await apiClient.put(url, body);
  return response.data?.content ?? response.data;
};

export const apiPatch = async <T>(url: string, body?: unknown): Promise<T> => {
  const response = await apiClient.patch(url, body);
  return response.data?.content ?? response.data;
};

export const apiDelete = async <T>(url: string): Promise<T> => {
  const response = await apiClient.delete(url);
  return response.data?.content ?? response.data;
};

export default apiClient;
